<template>
	<div
		class="cog-checkable cog-input"
		:class="[
			allowOther && 'cog-checkable--allow-other',
			hasColumns ? columnClasses : 'cog-checkable--inline',
			readonly && 'is-read-only',
			isCard && 'cog-checkable--card',
			isCard && 'cog-checkable--label-on-' + (!hasAtLeastOneImage || hideChoiceLabels ? 'bottom' : cardLabelPosition),
			hasAtLeastOneImage && choiceImageSizeClass,
			isPersonField && hasAtLeastOneImage && 'cog-checkable--avatars'
		]"
	>
		<template v-if="readonly">
			<template v-if="!selectedOptions.length">&nbsp;</template>
			<template v-else>
				<div
					v-for="(option, i) in selectedOptions"
					:key="option.value"
					:class="{
						'cog-inner-col__item' : hasColumns,
						'cog-checkable__item--no-label': hideChoiceLabels && hasImage(option)
					}"
					class="cog-checkable__item is-read-only"
					:style="isCard ? `order: ${optionOrder[i]}` : null"
				>
					<slot name="readonly-item" :item="option">
						<div
							class="cog-choice-option"
							:style="!isCard ? `order: ${optionOrder[i]}` : null"
						>
							<CChoiceImage
								v-if="hasImage(option)"
								v-bind="{option, isPersonField, choiceImageSize, fontSize}"
								@dimensions="processDimensions"
							/>
							<!-- Lack of whitespace for multi-line calculations -->
							<div v-if="!(hideChoiceLabels && hasImage(option))" class="cog-choice__readonly-label"><span class="cog-choice__value">{{ option.label ? option.label : option.displayValue | breakWords }}</span><span v-if="isCard && option.description" class="cog-choice__description">{{ cleanChoiceDescription(option.description) }}</span></div>
						</div>
					</slot>
				</div>
			</template>
		</template>
		<template v-else>
			<el-checkbox-group
				v-model="internalValue"
				:role="null"
				:label="null"
				@change="handleChange"
			>
				<el-checkbox
					v-for="(option, i) in options"
					:key="option.value"
					:label="option.value"
					class="cog-checkable__item cog-choice-option"
					:class="[
						{'cog-inner-col__item': hasColumns},
						{'cog-checkable__item--no-label': hideChoiceLabels && hasImage(option)}
					]"
					:style="`order: ${optionOrder[i]}`"
				>
					<template v-if="isCard">
						<ICardCheck class="cog-choice__card-checkmark" />
						<CChoiceImage
							v-if="hasImage(option)"
							v-bind="{option, isPersonField, choiceImageSize, fontSize}"
							@dimensions="processDimensions"
						/>
						<div v-if="!(hideChoiceLabels && hasImage(option))">
							<span class="cog-choice__value">{{ (option.label || option.displayValue) | breakWords }}</span>
							<span v-if="option.description" class="cog-choice__description">{{ cleanChoiceDescription(option.description) }}</span>
						</div>
					</template>
					<template v-if="!isCard && hasImage(option)" #image>
						<CChoiceImage
							v-if="hasImage(option)"
							v-bind="{option, isPersonField, choiceImageSize, fontSize}"
						/>
					</template>
					<template v-if="!isCard && !(hideChoiceLabels && hasImage(option))">
						<span class="cog-choice__value">{{ option.label ? option.label : option.displayValue | breakWords }}</span>
						<span v-if="option.description" class="cog-choice__description">{{ cleanChoiceDescription(option.description) }}</span>
					</template>
				</el-checkbox>
				<el-checkbox
					v-if="allowOther"
					:value="otherIsChecked"
					class="cog-checkable__allow-other cog-choice-option"
					:class="{ 'is-checked': otherIsChecked, 'cog-inner-col__item': hasColumns }"
					:style="`order: ${optionOrder[options.length]}`"
					:label="otherValue"
					@change="handleOtherCheckChange"
				>
					<el-input
						ref="other"
						v-model="otherValue"
						:placeholder="$resource('field-choice-other-fill-in')"
						:label="$resource('field-choice-other-fill-in')"
						@focus="handleOtherInputFocus"
						@blur="handleOtherInputBlur"
					/>
				</el-checkbox>
			</el-checkbox-group>
		</template>
	</div>
</template>

<script>
	import '../style/_checkables.scss';
	import '../style/_checkable-layout.scss';
	import '../style/_checkable-cards.scss';

	import Vue from 'vue';
	import ICardCheck from 'src/assets/card-check.svg';
	import { CheckboxGroup, Checkbox, Input } from '@cognitoforms/element-ui';
	import columnizedOptions from 'src/mixins/columnized-options';
	import resetComponentData from 'src/mixins/resetComponentData';
	import CChoiceImage from './ChoiceImage.vue';
	import { getChoiceImageSizeClass } from './use-choice-image-size';
	import { cleanChoiceDescription, alignChoiceItems, constrainCardDimensions } from './choiceUtils';

	Vue.use(CheckboxGroup);
	Vue.use(Checkbox);
	Vue.use(Input);

	export default {
		name: 'CCheckboxGroup',
		components: { CChoiceImage, ICardCheck },
		mixins: [columnizedOptions, resetComponentData],
		inject: ['$resource'],
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: {
				type: Array,
				default: () => []
			},
			readonly: Boolean,
			allowOther: Boolean,
			choiceImageSize: { type: Number, default: 0 },
			isCard: { type: Boolean, default: false },
			showChoiceImages: { type: Boolean, default: false },
			hideChoiceLabels: { type: Boolean, default: false },
			isPersonField: { type: Boolean, default: false },
			cardLabelPosition: { type: String, default: 'bottom' },
			imagesInChoiceFieldsEnabled: { type: Boolean, default: false }
		},
		emits: ['change'],
		data() {
			const otherValue = this.findOtherValue(this.value);
			return {
				internalValue: this.value,
				otherValue,
				otherIsChecked: this.allowOther && !!otherValue,
				resizeObserver: null,
				fontSize: undefined,
				dimensionConstraints: null,
				lookupDimensions: []
			};
		},
		computed: {
			selectedOptions() {
				const safeInternalValue = Array.isArray(this.internalValue) ? this.internalValue : [];
				const selected = this.options.filter(o => safeInternalValue.includes(o.value));
				if (this.otherIsChecked && this.otherValue)
					selected.push({ displayValue: this.otherValue, value: this.otherValue });
				return selected;
			},
			choiceImageSizeClass() {
				return getChoiceImageSizeClass(this.choiceImageSize);
			},
			hasAtLeastOneImage() {
				return this.options.some(item => this.hasImage(item));
			}
		},
		watch: {
			value(valueProp) {
				this.internalValue = valueProp;
				const newOtherValue = this.findOtherValue(valueProp);
				if (this.allowOther && newOtherValue) {
					this.otherIsChecked = true;
					this.otherValue = newOtherValue;
				}
			},
			dimensionConstraints(constraints) {
				if (constraints) {
					const imageContainers = this.$el.querySelectorAll('.cog-choice-image-container');
					imageContainers.forEach((container) => {
						if (constraints?.maxHeight)
							container.style.maxHeight = constraints.maxHeight;
						else if (constraints?.maxWidth)
							container.style.maxWidth = constraints.maxWidth;
					});
				}
			}
		},
		mounted() {
			this.fontSize = parseInt(getComputedStyle(this.$el).fontSize);

			if (this.isCard && this.options.some(opt => opt.choiceImageDimensions?.width && opt.choiceImageDimensions?.height))
				this.dimensionConstraints = constrainCardDimensions(this.options);

			this.resizeObserver = new ResizeObserver(() => {
				alignChoiceItems(this.$el);
			});
			this.resizeObserver.observe(this.$el);
		},
		beforeDestroy() {
			if (this.resizeObserver)
				this.resizeObserver.disconnect();
		},
		methods: {
			hasImage(item) {
				return item.svg || item.thumbnailImage || item.choiceThumbnailImage;
			},
			findOtherValue(selectedOptions) {
				const safeSelected = Array.isArray(selectedOptions) ? selectedOptions : (selectedOptions ? [selectedOptions] : []);
				return safeSelected.find(selected => !this.options.some(o => o.value === selected));
			},
			handleOtherCheckChange(isChecked) {
				this.otherIsChecked = isChecked;
				if (isChecked && !this.readonly && this.$refs.other)
					this.$refs.other.focus();
				this.handleChange(this.internalValue, null);
			},
			handleChange(value, ev) {
				this.internalValue = value;
				this.$emit('change', this.selectedOptions.map(o => o.value), ev);
			},
			handleOtherInputFocus() {
				if (!this.otherIsChecked) {
					this.updating = true;
					this.handleOtherCheckChange(true);
				}
			},
			handleOtherInputBlur(ev) {
				if (!this.otherValue) {
					this.otherIsChecked = false;
					this.handleChange(this.selectedOptions.map(o => o.value), ev);
				}

				// Raise change if an "other" value has been added or modified
				if (this.otherValue && this.value.indexOf(this.otherValue) < 0) {
					this.handleChange(this.internalValue, ev);
				}
			},
			cleanChoiceDescription,
			processDimensions(dimensions) {
				this.lookupDimensions.push(dimensions);
				if (this.lookupDimensions.length === this.options.filter(o => this.hasImage(o)).length)
					this.dimensionConstraints = constrainCardDimensions(this.lookupDimensions);
			}
		}
	};
</script>